import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { LuBath, LuBedDouble, LuHeart, LuRuler, LuGitCompare } from 'react-icons/lu'
import { formatArea, formatPriceWithQualifier, formatCurrency } from '../utils/formatters.js'
import { useSavedProperties } from '../hooks/useSavedProperties.js'
import { useComparison } from '../context/ComparisonContext.jsx'
import { useAffordability } from '../context/AffordabilityContext.jsx'

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000

const isNewListing = (listedAt) => {
  if (!listedAt) return false
  return Date.now() - new Date(listedAt).getTime() <= THREE_DAYS_MS
}

const AFFORDABILITY_TOOLTIPS = {
  in_budget: 'Within your mortgage budget',
  stretching: 'At the edge of your budget',
  over_budget: 'Over your mortgage budget',
}

const PropertyCard = ({ property, linkState, showCompare }) => {
  const { isSaved, toggleSaved } = useSavedProperties()
  const { isAuthenticated, loginWithRedirect } = useAuth0()
  const { isInComparison, addToComparison, removeFromComparison, isFull } = useComparison()
  const { hasProfile, getAffordability } = useAffordability()
  const saved = isSaved(property.id)
  const inComparison = isInComparison(property.id)
  const locationLabel = [property.location, property.city].filter(Boolean).join(', ')
  const affordability = hasProfile && property.price ? getAffordability(property.price, property.postcode) : null

  const handleSaveClick = (event) => {
    event.preventDefault()
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        loginWithRedirect?.({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        })
      } else {
        loginWithRedirect?.()
      }
      return
    }
    toggleSaved(property.id)
  }

  const canContactSeller = !property.hasAcceptedOffer
  const isNew = isNewListing(property.listedAt)
  const isPriceReduced = property.previousPrice != null && property.previousPrice > property.price

  return (
    <article className="property-card">
      <Link
        to={`/property/${property.id}`}
        className="property-card__image"
        aria-label={`View details for ${property.title}`}
        state={linkState}
      >
        <img src={property.heroImage} alt={property.title} loading="lazy" />
        <span className="property-card__tag">{(property.propertyType).replace("_", " ").toUpperCase()}</span>
        {isNew && <span className="property-card__new-badge">New</span>}
        <button
          type="button"
          className={`property-card__save ${saved ? 'is-saved' : ''}`}
          aria-label={saved ? 'Remove from saved homes' : 'Save this property'}
          onClick={handleSaveClick}
        >
          {saved ? (
            <LuHeart aria-hidden="true" style={{ fill: 'currentColor' }} />
          ) : (
            <LuHeart aria-hidden="true" style={{ fill: 'none' }} />
          )}
        </button>
      </Link>

      <div className="property-card__body">
        <span className="property-card__price">
          {formatPriceWithQualifier(property.price, property.priceQualifier)}
        </span>
        {property.hasAcceptedOffer && <span className="property-card__status pill pill--warning">Under offer</span>}
        {isPriceReduced && (
          <span className="property-card__price-reduced" title={`Reduced from ${formatCurrency(property.previousPrice)}`}>
            ↓ Was {formatCurrency(property.previousPrice)}
            {property.priceReducedAt && (
              <span className="property-card__price-reduced-date">
                {new Date(property.priceReducedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </span>
        )}
        {property.isNewHome && (
          <span className="property-card__flag property-card__flag--new-build">New build</span>
        )}
        {property.hasBuyingScheme && (
          <span className="property-card__flag property-card__flag--shared-ownership">Shared ownership</span>
        )}
        <strong>{property.title}</strong>
        <p>{locationLabel || property.location || property.city}</p>
        {typeof property.distanceMiles === 'number' && (
          <p className="property-card__distance">
            {property.distanceMiles < 0.5
              ? `${Math.round(property.distanceMiles * 1760)} yards away`
              : `${property.distanceMiles.toFixed(1)} miles away`}
          </p>
        )}

        <div className="property-card__stats">
          <span>
            <LuBedDouble aria-hidden="true" /> {property.beds}
          </span>
          <span>
            <LuBath aria-hidden="true" /> {property.baths}
          </span>
          <span>
            <LuRuler aria-hidden="true" /> {formatArea(property.area)}
          </span>
          {affordability && (
            <span
              className={`property-card__affordability property-card__affordability--${affordability.status}`}
              title={AFFORDABILITY_TOOLTIPS[affordability.status]}
              aria-label={AFFORDABILITY_TOOLTIPS[affordability.status]}
            >
              £
            </span>
          )}
        </div>

        <div className="property-card__actions">
          <Link style={{ whiteSpace: 'nowrap' }} to={`/property/${property.id}`} state={linkState}>
            View details
          </Link>
          {canContactSeller && (
            <Link style={{ whiteSpace: 'nowrap' }} to={`/property/${property.id}#messages`} state={linkState}>
              Contact seller
            </Link>
          )}
        </div>

        {showCompare && (
          <button
            type="button"
            className={`property-card__compare${inComparison ? ' is-comparing' : ''}`}
            aria-label={inComparison ? 'Remove from comparison' : 'Add to comparison'}
            aria-pressed={inComparison}
            disabled={!inComparison && isFull}
            onClick={() => inComparison ? removeFromComparison(property.id) : addToComparison(property.id)}
          >
            <LuGitCompare aria-hidden="true" />
            {inComparison ? 'Remove from compare' : isFull ? 'Comparison full' : 'Compare'}
          </button>
        )}
      </div>
    </article>
  )
}

export default PropertyCard
